import React, {useState} from 'react';
import {Box, Stack, Typography, Button, Table, TableBody, TableCell, TableRow, Accordion, AccordionSummary, AccordionDetails} from '@mui/material';
import {HugeiconsIcon} from '@hugeicons/react';
import {Copy01Icon, ChevronDownIcon} from '@hugeicons/core-free-icons';
import {toast} from 'react-toastify';

export const MCPPromptsSectionNew = ({ server }) => {
  const [expanded, setExpanded] = useState(null);

  // server.prompts is an object keyed by prompt name ({name: promptData}), not an array
  const promptEntries = Object.entries(server?.prompts || {}).map(([name, value]) => {
    if (typeof value === 'string') {
      return {name, description: value, arguments: null, metadata: null};
    }
    const {description, arguments: args, metadata, ...rest} = value || {};
    return {name, description, arguments: args, metadata: metadata || (Object.keys(rest).length ? rest : null)};
  });

  const handleCopyPrompt = (prompt) => {
    try {
      const promptDef = {name: prompt.name, description: prompt.description, arguments: prompt.arguments};
      navigator.clipboard.writeText(JSON.stringify(promptDef, null, 2));
      toast.success('Prompt definition copied');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  if (promptEntries.length === 0) {
    return (<Box sx={{textAlign: 'center', color: '#8A8AA0', py: 6}}><Typography sx={{fontSize: '14px'}}>No prompts available for this server</Typography></Box>);
  }

  return (
    <Stack gap={1}>
      {promptEntries.map((prompt, index) => {
        const isExpanded = expanded === (prompt.name || index);
        const argCount = prompt.arguments ? Object.keys(prompt.arguments).length : 0;

        return (
          <Accordion
            key={prompt.name || index} expanded={isExpanded} onChange={() => setExpanded(isExpanded ? null : (prompt.name || index))}
            sx={{
              backgroundColor: isExpanded ? 'rgba(132, 145, 247, 0.05)' : '#FAFAFD',
              borderRadius: '12px', '&:before': {display: 'none'}, transition: 'background-color 0.2s ease',
              '&:hover': {backgroundColor: isExpanded ? 'rgba(132, 145, 247, 0.07)' : '#F2F2F8'},
            }}
          >
            <AccordionSummary
              sx={{
                p: '15px 18px !important',
                '& .MuiAccordionSummary-content': {alignItems: 'center', gap: 1.25, m: '0 !important'},
                '& .MuiAccordionSummary-expandIconWrapper': {display: 'none'},
              }}
            >
              <Typography sx={{fontSize: '13.5px', fontWeight: 600, color: '#23264D', letterSpacing: '-0.01em', flex: 1}}>{prompt.name}</Typography>
              {argCount > 0 && (<Box sx={{px: 0.75, py: 0.5, backgroundColor: 'rgba(132, 145, 247, 0.1)', borderRadius: '6px', fontSize: '11px', fontWeight: 700, color: '#8491F7'}}>{argCount} args</Box>)}
              <Box sx={{
                width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center',
                justifyContent: 'center', bgcolor: isExpanded ? '#8491F7' : '#F0F0F6', color: isExpanded ? '#fff' : '#6B6B80',
                transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.3s ease, color 0.3s ease',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}>
                <HugeiconsIcon icon={ChevronDownIcon} size={15}/>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{p: '16px', borderTop: '1px solid #ECECF3', backgroundColor: '#fff'}}>
              <Stack gap={2}>
                {prompt.description && (<Box><Typography sx={{fontSize: '11px', fontWeight: 700, color: '#8491F7', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75}}>Description</Typography><Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>{prompt.description}</Typography></Box>)}

                {prompt.arguments && Object.keys(prompt.arguments).length > 0 && (<Box><Typography sx={{fontSize: '11px', fontWeight: 700, color: '#8491F7', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1}}>Arguments</Typography><Table size="small" sx={{'& .MuiTableCell-root': {borderBottom: '1px solid #ECECF3', px: 1.25, py: 0.75, fontSize: '13px'}, '& .MuiTableBody-root > .MuiTableRow-root:last-child .MuiTableCell-root': {borderBottom: 'none'}}}><TableBody>{Object.entries(prompt.arguments).map(([argName, argDef]) => (<TableRow key={argName} sx={{backgroundColor: '#FAFAFD'}}><TableCell sx={{fontWeight: 600, color: '#23264D'}}>{argName}</TableCell><TableCell sx={{color: '#8A8AA0', fontSize: '12px'}}>{typeof argDef === 'string' ? argDef : argDef.description || 'argument'}</TableCell></TableRow>))}</TableBody></Table></Box>)}

                {prompt.metadata && Object.keys(prompt.metadata).length > 0 && (<Box><Typography sx={{fontSize: '11px', fontWeight: 700, color: '#8491F7', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1}}>Metadata</Typography><Table size="small" sx={{'& .MuiTableCell-root': {borderBottom: '1px solid #ECECF3', px: 1.25, py: 0.75, fontSize: '13px'}, '& .MuiTableBody-root > .MuiTableRow-root:last-child .MuiTableCell-root': {borderBottom: 'none'}}}><TableBody>{Object.entries(prompt.metadata).map(([key, value]) => (<TableRow key={key} sx={{backgroundColor: '#FAFAFD'}}><TableCell sx={{fontWeight: 600, color: '#23264D'}}>{key}</TableCell><TableCell sx={{color: '#8A8AA0', wordBreak: 'break-word', fontSize: '12px', fontFamily: 'monospace'}}>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</TableCell></TableRow>))}</TableBody></Table></Box>)}

                <Button size="small" startIcon={<HugeiconsIcon icon={Copy01Icon} size={14} />} onClick={() => handleCopyPrompt(prompt)} sx={{alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700, fontSize: '13px', color: '#8491F7', '&:hover': {backgroundColor: 'rgba(132, 145, 247, 0.08)'}}} >Copy Definition</Button>
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
};
